import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, MessageCircle, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { api, imgUrl, preorderLabel, usd } from "@/lib/api";
import { useLang } from "@/i18n";

export default function Shop() {
  const [products, setProducts] = useState(null);
  const [category, setCategory] = useState("All");
  const { add } = useCart();
  const { t, lang } = useLang();

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => {
        setProducts([]);
        toast.error("Could not load products");
      });
  }, []);

  const categories = useMemo(
    () => ["All", ...new Set((products || []).map((p) => p.category))],
    [products]
  );

  const visible = useMemo(
    () =>
      (products || []).filter(
        (p) => category === "All" || p.category === category
      ),
    [products, category]
  );

  return (
    <main data-testid="shop-page" className="min-h-screen bg-[#0B1120] px-6 md:px-12 lg:px-24 pt-32 pb-24">
      <Reveal y={24}>
        <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">
          {t("shop_label")}
        </p>
        <h1 className="font-display font-semibold text-white text-4xl sm:text-6xl tracking-tight mb-4">
          {t("shop_title1")}
          <br />
          {t("shop_title2").replace(".", "")}<span className="text-[#FFC61A]">.</span>
        </h1>
        <p className="text-slate-400 max-w-xl mb-12">
          {t("shop_sub")}
        </p>
        <div className="flex flex-wrap gap-4 mb-12 -mt-4">
          <Link
            data-testid="shop-catalogue-link"
            to="/downloads"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#FFC61A] transition-colors duration-300 border-b border-white/20 hover:border-[#FFC61A] pb-1"
          >
            {t("nav_catalogue")} →
          </Link>
          <Link
            data-testid="shop-kit-builder-link"
            to="/kit-builder"
            className="inline-flex items-center gap-2 text-sm text-[#FFC61A] hover:text-white transition-colors duration-300 border-b border-[#FFC61A]/40 hover:border-white pb-1"
          >
            {t("kit_title")} →
          </Link>
          <Link
            data-testid="shop-cctv-kit-link"
            to="/cctv-kit-builder"
            className="inline-flex items-center gap-2 text-sm text-[#FFC61A] hover:text-white transition-colors duration-300 border-b border-[#FFC61A]/40 hover:border-white pb-1"
          >
            {t("cctvkit_title")} →
          </Link>
        </div>
      </Reveal>

      <div className="flex flex-wrap gap-3 mb-12" data-testid="category-filters">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => setCategory(c)}
            className={`text-xs tracking-wide uppercase px-5 py-2.5 border transition-colors duration-300 ${
              category === c
                ? "bg-[#ED1C24] border-[#ED1C24] text-white"
                : "border-white/15 text-slate-400 hover:border-[#ED1C24] hover:text-white"
            }`}
          >
            {c === "All" ? t("filter_all") : c}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {products === null
          ? Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-96 bg-white/[0.03] border border-white/10 animate-pulse" />
            ))
          : visible.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 0.06}>
                <div
                  data-testid={`product-card-${p.id}`}
                  className="group bg-[#0F172A] border border-white/10 hover:border-[#ED1C24]/60 hover:-translate-y-2 transition-[border-color,transform] duration-500 flex flex-col h-full"
                >
                  <Link to={`/product/${p.id}`} className="relative h-52 overflow-hidden block">
                    <img
                      src={imgUrl(p.image)}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {(() => {
                      if (p.availability === "preorder") {
                        return (
                          <span data-testid={`preorder-badge-${p.id}`} className="absolute top-3 left-3 bg-[#FFC61A] text-[#0B1120] text-[10px] font-semibold tracking-[0.1em] uppercase px-3 py-1">
                            {preorderLabel(p, t)}
                          </span>
                        );
                      }
                      const status = p.stock_status || (p.stock <= 5 ? "low_stock" : "in_stock");
                      if (status === "out_of_stock") {
                        return (
                          <span className="absolute top-3 left-3 bg-[#ED1C24] text-white text-[10px] font-semibold tracking-[0.1em] uppercase px-3 py-1">
                            {t("out_stock")}
                          </span>
                        );
                      }
                      if (status === "low_stock") {
                        return (
                          <span className="absolute top-3 left-3 bg-[#FFC61A] text-[#0B1120] text-[10px] font-semibold tracking-[0.1em] uppercase px-3 py-1">
                            {t("low_stock")}
                          </span>
                        );
                      }
                      return (
                        <span data-testid={`ready-badge-${p.id}`} className="absolute top-3 left-3 bg-emerald-500 text-white text-[10px] font-semibold tracking-[0.1em] uppercase px-3 py-1">
                          {t("ready_stock")}
                        </span>
                      );
                    })()}
                  </Link>
                  <div className="p-5 flex flex-col flex-1">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                      {p.category}{p.brand ? ` · ${p.brand}` : ""}
                    </p>
                    <h3 className="font-display font-medium text-white text-base leading-snug">
                      <Link to={`/product/${p.id}`} className="hover:text-[#FFC61A] transition-colors duration-300">
                        {p.name}
                      </Link>
                    </h3>
                    <p className="text-xs text-slate-500 leading-relaxed mt-2 line-clamp-2 flex-1">
                      {lang === "tet" && p.description_tet ? p.description_tet : p.description}
                    </p>
                    <div className="flex items-center justify-between mt-5">
                      <span className="font-display text-xl text-[#FFC61A]">{usd(p.price)}</span>
                      <div className="flex gap-2">
                        {p.catalogue && (
                          <a
                            data-testid={`product-catalogue-${p.id}`}
                            href={imgUrl(p.catalogue)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Download ${p.name} catalogue`}
                            title={t("download_catalogue")}
                            className="w-10 h-10 border border-white/15 flex items-center justify-center text-slate-400 hover:border-[#FFC61A] hover:text-[#FFC61A] transition-colors duration-300"
                          >
                            <FileDown size={16} />
                          </a>
                        )}
                        <a
                          data-testid={`product-whatsapp-${p.id}`}
                          href={`https://wa.me/67077138966?text=${encodeURIComponent(
                            `Hello KINOS, I want to buy: ${p.name} (${usd(p.price)})`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Buy ${p.name} via WhatsApp`}
                          className="w-10 h-10 border border-white/15 flex items-center justify-center text-[#25D366] hover:border-[#25D366] transition-colors duration-300"
                        >
                          <MessageCircle size={16} />
                        </a>
                        <button
                          data-testid={`product-add-to-cart-${p.id}`}
                          onClick={() => {
                            add(p);
                            toast.success(`${p.name} ${t("cart_added")}`);
                          }}
                          className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-xs font-medium px-4 py-2.5 transition-colors duration-300"
                        >
                          <ShoppingCart size={14} />
                          {t("add")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
      </div>

      {products !== null && visible.length === 0 && (
        <p data-testid="no-products-message" className="text-slate-400 text-center py-20">
          {t("shop_empty")}
        </p>
      )}
    </main>
  );
}
